/**
 * Aspect — Structure pricing (doors, windows, lights, switches, walls, floor)
 * --------------------------------------------------------------
 * Budget install prices for Tbilisi, Georgia (GEL / ლარი).
 */

import { wallLength } from "./wall_geometry.js";
import { aspectSalePrice } from "../currency.js";
import {
  finishLabel,
  DEFAULT_FLOOR_FINISH,
  DEFAULT_WET_FLOOR_FINISH,
  FINISHES,
} from "./texture_sets.js";

export const LIGHT_FIXTURE_PRICES = {
  ceiling: 42,
  pendant: 68,
  spot: 28,
  chandelier: 185,
};

export const SWITCH_PRICE = 15;

const round5 = (n) => Math.round(n / 5) * 5;

export function doorPrice(width, height) {
  const w = width ?? 0.9;
  const h = height ?? 2.1;
  return round5(85 + w * 50 + h * 25);
}

export function windowPrice(width, height) {
  const w = width ?? 1.2;
  const h = height ?? 1.2;
  return round5(100 + w * 40 + h * 30);
}

/** Soft furnishings for a window — priced by opening width (fabric + rod). */
export function curtainPrice(width, height) {
  const w = width ?? 1.2;
  const h = height ?? 2.2;
  return round5(55 + w * 45 + h * 10);
}

export function lightPrice(fixture) {
  const key = String(fixture ?? "ceiling").toLowerCase();
  return LIGHT_FIXTURE_PRICES[key] ?? LIGHT_FIXTURE_PRICES.ceiling;
}

export function floorAreaM2(room) {
  if (room?.floorArea > 0) return room.floorArea;
  const b = room?.bounds;
  if (!b?.min || !b?.max) return 0;
  return Math.max(0, (b.max[0] - b.min[0]) * (b.max[2] - b.min[2]));
}

/** Laminate / wood floor finish — GEL per m² (supply + install, Tbilisi budget). */
export function floorPrice(room) {
  return round5(floorAreaM2(room) * 26);
}

/** Wet-room tile / marble — GEL per m². */
export function wetFloorPricePerM2() {
  return 42;
}

/** Dry-room wood / parquet — GEL per m². */
export function dryFloorPricePerM2() {
  return 28;
}

export function isWetRoomType(type) {
  return /(bath|wc|toilet|shower|kitchen|laundry|wet|საპირფარეშ|სველ|აბაზან)/i.test(String(type ?? ""));
}

/** True when a room record should get the marble / wet-room floor overlay. */
export function isWetFloorRoom(room) {
  if (!room) return false;
  return isWetRoomType(room.type) || isWetRoomType(room.name) || isWetRoomType(room.label);
}

/** Painted gypsum ceiling / roof slab — GEL per m² (Tbilisi budget). */
export function ceilingPrice(room) {
  return round5(floorAreaM2(room) * 22);
}

/** Room ceiling height in meters. */
export function roomCeilingHeight(room) {
  const b = room?.bounds;
  if (b?.min && b?.max) return b.max[1] - b.min[1];
  return room?.dimensions?.height ?? 2.8;
}

/** Plaster wall — GEL per face m² (paint-ready, budget tier). */
export function wallPrice(wall, room) {
  const len = wall?.length ?? wallLength(wall);
  const ceilH = room?.dimensions?.height ?? 2.8;
  const h = Math.min(wall?.height ?? ceilH, ceilH);
  return round5(len * h * 20);
}

export function openingPrice(op) {
  if (!op) return 0;
  if (op.type === "window") return windowPrice(op.width, op.height);
  if (op.type === "door") return doorPrice(op.width, op.height);
  return 0;
}

export function windowMeta(op) {
  const w = op?.width ?? 1.2;
  const h = op?.height ?? 1.2;
  return {
    name: "Window",
    brand: "Apartment Modern",
    material: "Wood · glass",
    price: windowPrice(w, h),
    width: w,
    height: h,
    sill: op?.sill ?? 0.9,
  };
}

/** Hover-card product entry for Unity / procedural window curtains. */
export function curtainMeta(op) {
  const w = op?.width ?? 1.2;
  const sill = op?.sill ?? 0.9;
  const winH = op?.height ?? 1.2;
  // Fabric typically hangs floor-to-rod (above window head).
  const h = Math.max(1.6, sill + winH + 0.1);
  return {
    name: "Curtains",
    brand: "Apartment Modern",
    material: "Fabric · metal rod",
    price: curtainPrice(w, h),
    width: w,
    height: h,
  };
}

export function wallMeta(wall, room) {
  const len = wall?.length ?? wallLength(wall);
  const ceilH = room?.dimensions?.height ?? 2.8;
  const h = Math.min(wall?.height ?? ceilH, ceilH);
  return {
    name: wall?.view ? "Modern balcony" : "Interior Wall",
    brand: "Aspect Build",
    material: wall?.view ? "Stainless steel & safety glass" : "Plaster & paint",
    price: wallPrice(wall, room),
    length: len,
    height: h,
  };
}

export function floorMeta(home, zone = null) {
  const finishes = home?.finishes ?? {};
  const wet = zone ? isWetFloorRoom(zone) : false;
  const finishId = wet
    ? finishes.floorWet || DEFAULT_WET_FLOOR_FINISH
    : finishes.floor || DEFAULT_FLOOR_FINISH;
  const finish = FINISHES[finishId];
  const material = finish?.material ?? finishLabel(finishId) ?? (wet ? "Tile" : "Wood floor");
  const finishName = finishLabel(finishId) ?? material;

  let width = 0;
  let depth = 0;
  let area = 0;
  let label = finishName;

  if (zone) {
    width = Math.max(0, Number(zone.width) || 0);
    depth = Math.max(0, Number(zone.depth) || 0);
    area = width * depth;
    const roomName = String(zone.name || zone.type || "Room").trim() || "Room";
    label = `${roomName} · ${finishName}`;
  } else {
    area = floorAreaM2(home);
    const b = home?.bounds;
    width = b ? b.max[0] - b.min[0] : 0;
    depth = b ? b.max[2] - b.min[2] : 0;
  }

  const pricePerM2 = wet ? wetFloorPricePerM2() : dryFloorPricePerM2();
  return {
    name: label,
    brand: "Aspect Build",
    material,
    price: round5(area * pricePerM2),
    area,
    width,
    depth,
    finishId,
    wet,
  };
}

export function ceilingMeta(room) {
  const area = floorAreaM2(room);
  const b = room?.bounds;
  const w = b ? b.max[0] - b.min[0] : 0;
  const d = b ? b.max[2] - b.min[2] : 0;
  const h = roomCeilingHeight(room);
  return {
    name: "Ceiling / Roof",
    brand: "Aspect Build",
    material: "Painted gypsum board",
    price: ceilingPrice(room),
    area,
    width: w,
    depth: d,
    height: h,
  };
}

export function lightMeta(fixture) {
  const key = String(fixture ?? "ceiling").toLowerCase();
  const labels = {
    ceiling: "Flush Ceiling Light",
    pendant: "Pendant Lamp",
    spot: "Recessed Spot",
    chandelier: "Chandelier",
  };
  return {
    name: labels[key] ?? labels.ceiling,
    brand: "Lumen & Co.",
    material: "LED fixture",
    price: lightPrice(fixture),
    fixture: key,
  };
}

export function switchMeta() {
  return {
    name: "Light Switch",
    brand: "Lumen & Co.",
    material: "Wall plate",
    price: SWITCH_PRICE,
  };
}

/**
 * Sum price + item count for the full built shell (not furniture).
 * `saleTotal` applies the same deterministic per-item Aspect sale shown on
 * hover cards (aspectSalePrice keyed by the item's stable id), so the
 * old-vs-new savings summary agrees with what shoppers see per object.
 */
export function structureBudget(room) {
  if (!room) return { total: 0, count: 0, saleTotal: 0 };
  let total = 0;
  let saleTotal = 0;
  let count = 0;
  const add = (price, saleKey) => {
    total += price;
    saleTotal += aspectSalePrice(price, saleKey)?.sale ?? price;
    count += 1;
  };

  if (floorAreaM2(room) > 0) {
    add(floorPrice(room), "floor");
    add(ceilingPrice(room), "ceiling");
  }

  for (const w of room.walls ?? []) {
    if (wallLength(w) < 0.02) continue;
    add(wallPrice(w, room), w.id);
  }

  for (const op of room.openings ?? []) {
    if (op.type !== "door" && op.type !== "window") continue;
    add(openingPrice(op), op.id);
    if (op.type === "window" && op.curtain !== false) {
      const sill = op.sill ?? 0.9;
      const winH = op.height ?? 1.2;
      add(curtainPrice(op.width, Math.max(1.6, sill + winH + 0.1)), `curtain:${op.id}`);
    }
  }
  for (const l of room.lights ?? []) {
    add(lightPrice(l.fixture), l.id);
  }
  for (const _s of room.switches ?? []) {
    add(SWITCH_PRICE, "switch");
  }
  return { total, count, saleTotal };
}

export default structureBudget;
